import { showToast } from './Toast.js';
import type { ComplianceSession } from '../../core/types.js';

type NavigateFn = (route: { name: string; id?: string } & Record<string, unknown>) => void;

export async function renderDashboard(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title">Dashboard</h1>
          <p class="page-subtitle">Compliance oversight for all AI-generated documents</p>
        </div>
        <button class="btn btn-gold btn-lg" id="new-session-btn">
          ✦ New Session
        </button>
      </div>
    </div>

    <div class="stats-grid" id="stats-grid">
      <div class="stat-card">
        <div class="stat-label">Total Sessions</div>
        <div class="stat-value" id="stat-total">—</div>
        <div class="stat-sub">All time</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Certified</div>
        <div class="stat-value" id="stat-certified" style="color:var(--success)">—</div>
        <div class="stat-sub">Notarized documents</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Drafts</div>
        <div class="stat-value" id="stat-draft" style="color:var(--warning)">—</div>
        <div class="stat-sub">Pending certification</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Avg. Human Edit</div>
        <div class="stat-value" id="stat-edit">—</div>
        <div class="stat-sub">Across all sessions</div>
      </div>
    </div>

    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:20px">
        <h2 style="font-size:15px;font-weight:700">Recent Sessions</h2>
        <input type="text" id="search-input" placeholder="Search sessions..." style="width:220px;padding:7px 12px;font-size:12px" />
      </div>
      <div class="session-list" id="session-list">
        <div class="empty-state">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  `;

  document.getElementById('new-session-btn')!.addEventListener('click', () => {
    navigate({ name: 'new-session' });
  });

  await loadSessions(navigate);

  document.getElementById('search-input')!.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    document.querySelectorAll('.session-item').forEach((item) => {
      const title = item.querySelector('.session-item-title')?.textContent?.toLowerCase() ?? '';
      (item as HTMLElement).style.display = title.includes(q) ? '' : 'none';
    });
  });
}

async function loadSessions(navigate: NavigateFn): Promise<void> {
  const listEl = document.getElementById('session-list')!;

  try {
    const sessions: ComplianceSession[] = await (window as unknown as { preuve: { getSessions: () => Promise<ComplianceSession[]> } }).preuve.getSessions();

    const total = sessions.length;
    const certified = sessions.filter((s) => s.status === 'certified').length;
    const draft = sessions.filter((s) => s.status === 'draft').length;
    const avgEdit = total > 0 ? Math.round(sessions.reduce((sum, s) => sum + s.humanEditDelta, 0) / total) : 0;

    document.getElementById('stat-total')!.textContent = String(total);
    document.getElementById('stat-certified')!.textContent = String(certified);
    document.getElementById('stat-draft')!.textContent = String(draft);
    document.getElementById('stat-edit')!.textContent = `${avgEdit}%`;

    if (sessions.length === 0) {
      listEl.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">◈</div>
          <div class="empty-state-title">No Sessions Yet</div>
          <div class="empty-state-desc">Create your first compliance session to start<br>notarizing AI-generated documents.</div>
        </div>
      `;
      return;
    }

    listEl.innerHTML = sessions.map((s) => `
      <div class="session-item" data-id="${s.id}">
        <div class="session-item-main">
          <div class="session-item-title">${escHtml(s.documentTitle)}</div>
          <div class="session-item-meta">
            ${s.provider.toUpperCase()} · ${s.modelId} · Human edit: ${s.humanEditDelta}% ·
            ${new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div class="session-item-actions">
          <span class="badge badge-${s.status}">${s.status}</span>
        </div>
      </div>
    `).join('');

    listEl.querySelectorAll('.session-item').forEach((item) => {
      item.addEventListener('click', () => {
        navigate({ name: 'session', id: item.getAttribute('data-id')! });
      });
    });
  } catch {
    listEl.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠</div>
        <div class="empty-state-title">Not Connected</div>
        <div class="empty-state-desc">Running in browser preview mode. Launch via Electron to connect to the audit database.</div>
      </div>
    `;
    document.getElementById('stat-total')!.textContent = '0';
    document.getElementById('stat-certified')!.textContent = '0';
    document.getElementById('stat-draft')!.textContent = '0';
    document.getElementById('stat-edit')!.textContent = '0%';
  }
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
